import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, X, Image as ImageIcon, Video, Eye, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { formatDistanceToNow } from "date-fns";

function StoryViewer({ userStories, initialIndex, onClose }) {
  const [currentUserIdx, setCurrentUserIdx] = useState(initialIndex);
  const [currentStoryIdx, setCurrentStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const currentUser = userStories[currentUserIdx];
  const currentStory = currentUser?.stories?.[currentStoryIdx];
  const STORY_DURATION = currentStory?.media?.media_type === "video" ? 15000 : 5000;

  useEffect(() => {
    if (!currentStory) return;
    axios.post(`${API}/stories/${currentStory.story_id}/view`, {}, { withCredentials: true }).catch(() => {});
    setProgress(0);
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / STORY_DURATION) * 100, 100));
      if (elapsed >= STORY_DURATION) {
        clearInterval(timerRef.current);
        goNext();
      }
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [currentUserIdx, currentStoryIdx]);

  const goNext = () => {
    if (currentStoryIdx < currentUser.stories.length - 1) {
      setCurrentStoryIdx(prev => prev + 1);
    } else if (currentUserIdx < userStories.length - 1) {
      setCurrentUserIdx(prev => prev + 1);
      setCurrentStoryIdx(0);
    } else {
      onClose();
    }
  };

  const goPrev = () => {
    if (currentStoryIdx > 0) {
      setCurrentStoryIdx(prev => prev - 1);
    } else if (currentUserIdx > 0) {
      setCurrentUserIdx(prev => prev - 1);
      setCurrentStoryIdx(0);
    }
  };

  if (!currentStory) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="story-viewer">
      <button onClick={onClose} className="absolute top-4 right-4 z-50 text-white/80 hover:text-white"><X className="w-8 h-8" /></button>
      <button onClick={goPrev} className="absolute left-4 z-50 text-white/60 hover:text-white"><ChevronLeft className="w-10 h-10" /></button>
      <button onClick={goNext} className="absolute right-4 z-50 text-white/60 hover:text-white"><ChevronRight className="w-10 h-10" /></button>

      <div className="relative w-full max-w-md h-[80vh] max-h-[700px] bg-slate-900 rounded-2xl overflow-hidden">
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-40 flex gap-1 p-2">
          {currentUser.stories.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: i < currentStoryIdx ? '100%' : i === currentStoryIdx ? `${progress}%` : '0%' }} />
            </div>
          ))}
        </div>

        {/* User info */}
        <div className="absolute top-6 left-3 right-3 z-40 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-deep-blue flex items-center justify-center text-white text-xs font-bold border-2 border-white">
            {currentUser.user_picture ? <img src={currentUser.user_picture} alt="" className="w-full h-full rounded-full object-cover" /> : currentUser.user_name?.charAt(0)?.toUpperCase()}
          </div>
          <span className="text-white text-sm font-semibold">{currentUser.user_name}</span>
          <span className="text-white/50 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />{currentStory.created_at ? formatDistanceToNow(new Date(currentStory.created_at), { addSuffix: true }) : ""}</span>
        </div>

        {/* Story content */}
        <div className="w-full h-full flex items-center justify-center" onClick={(e) => { e.clientX < window.innerWidth / 2 ? goPrev() : goNext(); }}>
          {currentStory.media?.url ? (
            currentStory.media.media_type === "video" ? (
              <video src={`${API.replace('/api', '')}${currentStory.media.url}`} className="w-full h-full object-contain" autoPlay muted />
            ) : (
              <img src={`${API.replace('/api', '')}${currentStory.media.url}`} alt="" className="w-full h-full object-contain" />
            )
          ) : null}
          {currentStory.content && (
            <div className={`absolute inset-0 flex items-center justify-center p-8 ${!currentStory.media ? 'bg-gradient-to-br from-deep-blue to-awareness-blue' : ''}`}>
              <p className="text-white text-xl font-semibold text-center leading-relaxed drop-shadow-lg">{currentStory.content}</p>
            </div>
          )}
        </div>

        {/* View count */}
        <div className="absolute bottom-4 left-4 z-40 flex items-center gap-1 text-white/60 text-xs">
          <Eye className="w-3 h-3" /> {currentStory.view_count || 0} views
        </div>
      </div>
    </div>
  );
}

export default function StoriesBar() {
  const { user } = useAuth();
  const [userStories, setUserStories] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [storyContent, setStoryContent] = useState("");
  const [storyFile, setStoryFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { fetchStories(); }, []);

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API}/stories`);
      setUserStories(res.data || []);
    } catch { console.error("Failed to fetch stories"); }
  };

  const handleCreate = async () => {
    if (!storyContent.trim() && !storyFile) { toast.error("Add text or media"); return; }
    setPosting(true);
    const fd = new FormData();
    fd.append("content", storyContent);
    if (storyFile) fd.append("file", storyFile);
    try {
      await axios.post(`${API}/stories`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Story posted!");
      setCreateOpen(false);
      setStoryContent("");
      setStoryFile(null);
      setPreview(null);
      fetchStories();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to post story"); }
    finally { setPosting(false); }
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setStoryFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview({ type: "image", src: ev.target.result });
      reader.readAsDataURL(f);
    } else {
      setPreview({ type: "video", name: f.name });
    }
  };

  const openViewer = (idx) => { setViewerIndex(idx); setViewerOpen(true); };

  const hasMyStory = user && userStories.some(u => u.user_id === user.user_id);

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide" data-testid="stories-bar">
        {/* Add Story Button */}
        {user && (
          <button onClick={() => setCreateOpen(true)} className="flex flex-col items-center gap-1 shrink-0" data-testid="create-story-btn">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${hasMyStory ? 'bg-gradient-to-br from-awareness-blue to-deep-blue' : 'bg-slate-200 border-2 border-dashed border-slate-400'}`}>
              <Plus className={`w-6 h-6 ${hasMyStory ? 'text-white' : 'text-slate-500'}`} />
            </div>
            <span className="text-[10px] text-slate-500 font-medium">Your Story</span>
          </button>
        )}

        {/* User Story Bubbles */}
        {userStories.map((us, idx) => (
          <button key={us.user_id} onClick={() => openViewer(idx)} className="flex flex-col items-center gap-1 shrink-0" data-testid={`story-bubble-${us.user_id}`}>
            <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-br from-awareness-blue via-blue-500 to-deep-blue">
              <div className="w-full h-full rounded-full bg-white p-[2px]">
                <div className="w-full h-full rounded-full bg-deep-blue flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                  {us.user_picture ? <img src={us.user_picture} alt="" className="w-full h-full object-cover" /> : us.user_name?.charAt(0)?.toUpperCase()}
                </div>
              </div>
            </div>
            <span className="text-[10px] text-slate-600 font-medium max-w-[64px] truncate">{us.user_name?.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* Story Viewer */}
      {viewerOpen && userStories.length > 0 && (
        <StoryViewer userStories={userStories} initialIndex={viewerIndex} onClose={() => setViewerOpen(false)} />
      )}

      {/* Create Story Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-story-dialog">
          <h3 className="text-lg font-bold text-deep-blue mb-4">Create Story</h3>
          <Textarea value={storyContent} onChange={(e) => setStoryContent(e.target.value)} placeholder="What's on your mind?" className="min-h-[80px]" data-testid="story-text-input" />
          {preview && (
            <div className="relative mt-2">
              {preview.type === "image" ? (
                <img src={preview.src} alt="" className="max-h-40 rounded-lg object-contain" />
              ) : (
                <div className="bg-slate-100 rounded-lg p-3 flex items-center gap-2"><Video className="w-5 h-5" /><span className="text-sm">{preview.name}</span></div>
              )}
              <button onClick={() => { setStoryFile(null); setPreview(null); }} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5"><X className="w-4 h-4" /></button>
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFile} />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="story-add-media"><ImageIcon className="w-4 h-4 mr-1" /> Add Media</Button>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={posting || (!storyContent.trim() && !storyFile)} className="bg-deep-blue text-white" data-testid="story-submit-btn">{posting ? "Posting..." : "Share Story"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
